import React, { useState, useMemo, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import TransactionFilters from "@/shared/components/transactions/TransactionFilters";
import AdminTransactionTable from "@/shared/components/admin/AdminTransactionTable";
import TransactionDetailPanel from "@/shared/components/admin/TransactionDetailPanel";
import { Button } from "@/shared/components/ui/button";
import { ChevronLeft, ChevronRight, RefreshCw, Download } from "lucide-react";
import * as adminService from "@/shared/api/services/adminService";
import * as providerService from "@/shared/api/services/providerService";
import * as transactionService from "@/shared/api/services/transactionService";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import { mapGlobalTransactions } from "@/shared/utils/transactionMappers";

export default function Transactions() {
  const { t } = useLanguage();
  const [filters, setFilters] = useState({
    search: "",
    engine: "all",
    reseller: "all",
    merchant: "all",
    provider: "all",
    status: "all",
    type: "all",
    currency: "all",
    location: "all",
    dateFrom: null,
    dateTo: null,
  });
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [page, setPage] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [merchantIds, setMerchantIds] = useState([]);
  const [search, setSearch] = useState("");
  const pageSize = 10;

  const {
    data: paginationResult,
    isLoading: isLoadingTransactions,
    refetch: refetchTransactions,
    error: transactionsError,
  } = useQuery({
    queryKey: [
      "admin-global-transactions",
      page,
      pageSize,
      search,
      filters,
      merchantIds,
    ],
    queryFn: () => {
      const backendFilters = {};
      if (filters.status !== "all")
        backendFilters.status = filters.status;
      if (filters.type !== "all") backendFilters.type = filters.type;
      if (filters.provider !== "all")
        backendFilters.provider = filters.provider;
      if (filters.currency !== "all")
        backendFilters.currency = filters.currency;
      if (filters.location !== "all")
        backendFilters.location = filters.location;
      if (filters.dateFrom) backendFilters.startDate = filters.dateFrom;
      if (filters.dateTo) {
        const endOfToDay = new Date(filters.dateTo);
        endOfToDay.setHours(23, 59, 59, 999);
        backendFilters.endDate = endOfToDay.toISOString();
      }

      return transactionService.getPaginatedTransactions(
        page + 1,
        pageSize,
        search,
        backendFilters,
        merchantIds,
      );
    },
    refetchInterval: 5000,
    keepPreviousData: true,
  });

  const handleEntityFilterChange = (type, value) => {
    setFilters((prev) => ({
      ...prev,
      [type]: value,
    }));
    setPage(0);
  };

  const isLoading = isLoadingTransactions;

  const { data: engines = [] } = useQuery({
    queryKey: ["engines"],
    queryFn: () => adminService.getAllEngines(),
  });

  const { data: merchants = [] } = useQuery({
    queryKey: ["merchants"],
    queryFn: () => adminService.getAllMerchants(),
  });

  const { data: resellers = [] } = useQuery({
    queryKey: ["resellers"],
    queryFn: () => adminService.getAllResellers(),
  });

  const { data: providers = [] } = useQuery({
    queryKey: ["providers"],
    queryFn: () => providerService.getAllProviders(),
  });

  const { data: uniqueProviderNames = [] } = useQuery({
    queryKey: ["all-transaction-providers"],
    queryFn: () => transactionService.getUniqueProviders(),
  });

  useEffect(() => {
    const hasEntityFilters =
      filters.engine !== "all" ||
      filters.reseller !== "all" ||
      filters.merchant !== "all";

    if (!hasEntityFilters) {
      setMerchantIds([]);
      return;
    }

    const merchantIdSet = new Set();

    if (filters.engine !== "all") {
      const resellerIdsUnderEngine = resellers
        .filter((r) => r.createdByEngineId === filters.engine)
        .map((r) => r.id);

      merchants
        .filter(
          (m) =>
            m.createdByEngineId === filters.engine ||
            resellerIdsUnderEngine.includes(m.createdByResellerId),
        )
        .forEach((m) => merchantIdSet.add(m.id));
    }

    if (filters.reseller !== "all") {
      merchants
        .filter((m) => m.createdByResellerId === filters.reseller)
        .forEach((m) => merchantIdSet.add(m.id));
    }

    if (filters.merchant !== "all") {
      merchantIdSet.add(filters.merchant);
    }

    const nextMerchantIds = Array.from(merchantIdSet);
    const normalizedIds =
      nextMerchantIds.length > 0 ? nextMerchantIds : ["NO_MERCHANT_FOUND"];

    setMerchantIds((prev) => {
      if (
        prev.length === normalizedIds.length &&
        prev.every((id, index) => id === normalizedIds[index])
      ) {
        return prev;
      }
      return normalizedIds;
    });
  }, [
    filters.engine,
    filters.reseller,
    filters.merchant,
    merchants,
    resellers,
  ]);

  const rawTransactions = paginationResult?.data || [];
  const totalTransactions = paginationResult?.total || 0;
  const totalPages = paginationResult?.totalPages || 1;

  const combinedData = React.useMemo(() => {
    return mapGlobalTransactions(rawTransactions);
  }, [rawTransactions]);

  const providerOptions = useMemo(() => {
    const options = [];
    const seen = new Set();

    const addOption = (value, label, aliases = []) => {
      const normalizedValue = String(value || "").trim();
      if (!normalizedValue) return;
      if (seen.has(normalizedValue)) return;

      seen.add(normalizedValue);
      options.push({
        value: normalizedValue,
        label: label || normalizedValue,
        aliases: [normalizedValue, ...(aliases || [])].filter(Boolean),
      });
    };

    providers.forEach((p) => {
      const label = p.nameEn || p.name || p.code;
      addOption(p.id, label, [p.code, p.name, p.nameEn]);
    });

    combinedData.forEach((tx) => {
      const name =
        tx.client_bank_name || tx.bank_name || tx.provider_code || tx.bank_code;
      const provider = providers.find(
        (p) =>
          p.code === tx.provider_code ||
          p.name === name ||
          p.nameEn === name ||
          p.code === tx.bank_code,
      );
      const value = provider ? provider.id : name;
      addOption(value, name, [tx.provider_code, tx.bank_code]);
    });

    uniqueProviderNames.forEach((entry) => {
      const name =
        typeof entry === "string"
          ? entry
          : entry?.bankName || entry?.name || entry?.provider || entry?.value;

      const provider = providers.find(
        (p) => p.name === name || p.nameEn === name || p.code === name,
      );
      const value = provider ? provider.id : name;
      addOption(value, name);
    });

    return options.sort((a, b) => a.label.localeCompare(b.label));
  }, [providers, combinedData, uniqueProviderNames]);

  const paginatedTransactions = combinedData;

  const handleReset = () => {
    setFilters({
      search: "",
      engine: "all",
      reseller: "all",
      merchant: "all",
      provider: "all",
      status: "all",
      type: "all",
      currency: "all",
      location: "all",
      dateFrom: null,
      dateTo: null,
    });
    setMerchantIds([]);
    setSearch("");
    setPage(0);
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await refetchTransactions();
    } catch (error) {
      console.error("Error refreshing data:", error);
    } finally {
      setTimeout(() => setIsRefreshing(false), 500);
    }
  };

  const handleExport = () => {
    const headers = [
      t("type"),
      t("paymentId"),
      t("reference"),
      t("merchant"),
      t("reseller"),
      t("provider"),
      t("amount"),
      t("status"),
      t("date"),
    ];
    const rows = combinedData.map((tx) => [
      tx.type === "WITHDRAWAL" ? t("withdrawal") : t("deposit"),
      tx.transaction_id || tx.payment_id || tx.id,
      tx.reference ||
        (tx.type === "WITHDRAWAL" ? tx.bank_account || "" : "") ||
        "",
      tx.merchant?.name || tx.merchant?.code || "",
      tx.reseller?.name || "Direct",
      tx.provider?.name || tx.client_bank_name || "",
      (tx.ammount ?? tx.amount ?? 0).toString(),
      tx.status,
      tx.created_date || tx.createdAt,
    ]);
    const csv = [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `transactions-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
  };

  const selectedMerchant = selectedTransaction
    ? selectedTransaction.merchant?.id
      ? selectedTransaction.merchant
      : selectedTransaction.merchant_id
        ? merchants.find((m) => m.id === selectedTransaction.merchant_id)
        : null
    : null;
  const selectedReseller = selectedTransaction
    ? selectedTransaction.reseller?.id
      ? selectedTransaction.reseller
      : selectedTransaction.reseller_id
        ? resellers.find((r) => r.id === selectedTransaction.reseller_id) || {
            id: selectedTransaction.reseller_id,
            name:
              selectedTransaction.reseller?.name ||
              selectedTransaction.reseller_name,
          }
        : selectedMerchant?.createdByResellerId
          ? resellers.find((r) => r.id === selectedMerchant.createdByResellerId)
          : null
    : null;
  const selectedEngine = null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            {t("transactions")}
          </h1>
          <p className="text-slate-500">
            {isLoadingTransactions ? (
              <Skeleton width={120} />
            ) : (
              `${totalTransactions.toLocaleString()} ${t("transactionsFound")}`
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="gap-2"
          >
            <RefreshCw
              className={`h-4 w-4 ${isRefreshing ? "animate-spin" : ""}`}
            />
            {t("refresh")}
          </Button>
          <Button variant="outline" onClick={handleExport} className="gap-2">
            <Download className="h-4 w-4" />
            {t("export")}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <TransactionFilters
        filters={filters}
        onFilterChange={(f) => {
          setSearch(f.search);
          setFilters(f);
          setPage(0);
        }}
        merchants={merchants}
        resellers={resellers}
        engines={engines}
        providers={providerOptions}
        onExport={handleExport}
        onReset={handleReset}
        onEntityFilterChange={handleEntityFilterChange}
      />

      {/* Table */}
      <AdminTransactionTable
        transactions={paginatedTransactions}
        isLoading={isLoading}
        onView={setSelectedTransaction}
        merchants={merchants}
        resellers={resellers}
        engines={engines}
        showEngine={true}
        showReseller={true}
        isQRCode={false}
        mixedTypes={true}
      />

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-500">
            {t("showing")} {page * pageSize + 1} {t("to")}{" "}
            {Math.min((page + 1) * pageSize, totalTransactions)} {t("of")}{" "}
            {totalTransactions.toLocaleString()} {t("transactions")}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(page - 1)}
              disabled={page === 0}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm text-slate-600">
              {t("page")} {page + 1} {t("of")} {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(page + 1)}
              disabled={page >= totalPages - 1}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Transaction Detail Panel */}
      <TransactionDetailPanel
        transaction={selectedTransaction}
        open={!!selectedTransaction}
        onClose={() => setSelectedTransaction(null)}
        engine={selectedEngine}
        reseller={selectedReseller}
        merchant={selectedMerchant}
        isQRCode={selectedTransaction?.type !== "withdrawal"}
      />
    </div>
  );
}
